import * as React from "react";

import { AsyncComponent2 } from "@impero-com/common/components/asyncComponent";
import {
    toColumn,
    DEFAULT_COLUMN_BUILDER_ID,
    DATA_TABLE_MODE_VIEWPORT,
    ColumnBuilder,
} from "@impero-com/common/components/dataTable";
import { ProtectedLink } from "@impero-com/common/components/protectedLink";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as Cmp from "@impero-com/common/utils/comparison";
import {
    ArrayDataSource,
    DataExtractor,
    DataView,
    ExtractorExpander,
    PartialData,
} from "@impero-com/common/utils/dataView";
import { formatUserName } from "@impero-com/common/utils/formatting";
import withI18n, { t } from "@impero-com/common/utils/i18n";
import {
    DataViewIndex,
    SimpleAndFilter,
} from "@impero-com/common/utils/indexing";
import { set, clone, assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import { emitNotification, PageSpinner } from "@impero-com/ui";
import memoizeOne from "memoize-one";

import {
    getControlPrograms,
    getControlProgramsTags,
    patchControlPrograms,
} from "api/generated/control-program/control-program";
import type { ControlProgramId, UserProfile } from "api/generated/model";
import {
    UserId,
    ControlProgramOverviewResourceListDto as ControlProgramOverviewResources,
    UpdatableControlPrograms,
    AvailableActions,
    UserOverview,
    AdminEditViewAccessRights,
    StandardAccessRight,
    EntityView,
    ControlProgramTagViewListDto as ControlProgramTags,
} from "api/generated/model";
import { AccessRightTags } from "components/accessRightTags";
import { EntitiesCell } from "components/entitiesCell/EntitiesCell";
import { TagList } from "components/tag/tagList";
import { Tag, TagsData } from "components/tag/types";
import { CP_EXTRACTOR_KEYS } from "org/control/controlProgram/extractorKeys";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import { buildControlActivitiesListUrl } from "org/controlActivities/urlUtils";
import {
    PageStateManager,
    RenderContentArgs,
} from "org/pageState/components/pageStateManager";
import { pageStateControlProgramsCodecs } from "org/pageState/convert/controlPrograms";
import { IdAndName } from "org/pageState/models";
import { PAGE_KEY } from "org/pageState/pageKey";
import { PageFilterAndColumns } from "org/pageState/types";
import { splitPageStateWithCallback } from "org/pageState/utils";
import {
    formatAccessRight,
    ALL_ACCESS_RIGHTS,
    accessRightCategoryExtractorId,
    isAccessRightCategoryExtractorId,
} from "utils/accessRights";
import { ACCESS_RIGHTS } from "utils/accessRights/constants";
import {
    UserAndRights,
    ControlProgramAccessRights,
} from "utils/accessRights/models";
import { downgradeExtractorType } from "utils/dataView";
import { WrappedFormUtils } from "utils/form";
import { Guid } from "utils/guid";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import {
    DEFAULT_CATEGORY_METHODS,
    TagLookupData,
    isTagCategoriesExtractorId,
    adjustFiltersWithCategoryExtractorId,
    buildTagCategoryExtractorExpanderForNonGroupedTagsWithIds,
    createTagNameLookupFn,
    createCategoryNameLookupFn,
    AbstractTag,
    AbstractTagCategory,
    buildCategoryExtractorsAndExpandersFromTagsAndFilter,
} from "utils/tag";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";
import { ControlResources } from "../resources";
import {
    ControlModuleApplicationBar,
    PAGE_CONTROL_PROGRAMS,
} from "../sideMenu";
import { ControlProgramStatus, formatControlProgramStatus } from "../utils";

import {
    ManageAccessRightsForm,
    ManageAccessRightsFormValues,
} from "./accessRights";
import { ChangeDetailsForm, ChangeDetailsFormValues } from "./details";
import { ControlProgramDataRow } from "./types";
import { adaptTags } from "./utils";

interface ControlProgramsListProps {
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile | null;
    availableActions: AvailableActions;
    controlEntitiesEnabled: boolean;
    buildActivitiesUrl: (controlProgramId: ControlProgramId) => string;
}

interface ControlProgramsListCompState {
    status: ControlProgramsListCompStatus;
}

type ControlProgramsListCompStatus =
    | { type: "DEFAULT" }
    | { type: "SUBMITTING" };

const ENTITIES_EXTRACTOR_ID = "entities";

const CONTROL_PROGRAM_COLUMN_BUILDERS: {
    [extractorId: string]: ColumnBuilder<ControlProgramDataRow, any>;
} = {
    [DEFAULT_COLUMN_BUILDER_ID]: (extractor) => {
        if (
            DEFAULT_CATEGORY_METHODS.isCategoryExtractorId(extractor.id) ||
            isAccessRightCategoryExtractorId(extractor.id) ||
            isTagCategoriesExtractorId(extractor.id)
        ) {
            return {
                display: "NEVER",
            };
        }
        return toColumn(extractor, {
            display: "DEFAULT_VISIBLE",
        });
    },
    [CP_EXTRACTOR_KEYS.NAME]: (_extractor) => ({
        getFormattedValue: (row) => {
            return (
                <ProtectedLink
                    href={buildControlProgramViewUrl(
                        row.controlProgram.resource.controlProgram.id
                    )}
                    hasAccess={row.controlProgram.actions.view}
                >
                    {row.controlProgram.resource.controlProgram.name}
                </ProtectedLink>
            );
        },
    }),
    [CP_EXTRACTOR_KEYS.ACCESS_RIGHTS]: (_extractor) => ({
        getFormattedValue: (row) => {
            return <AccessRightTags {...row.accessRights} />;
        },
    }),
    [CP_EXTRACTOR_KEYS.TAGS]: (_extractor) => ({
        getFormattedValue: (row) => {
            return <TagList mode="HOVER" {...row.tags} />;
        },
    }),
    [CP_EXTRACTOR_KEYS.CONTROL_COUNT]: (_extractor) => ({
        layout: { fixedWidth: 110 },
    }),
    [CP_EXTRACTOR_KEYS.STATUS]: (_extractor) => ({
        layout: { fixedWidth: 110 },
    }),
    [ENTITIES_EXTRACTOR_ID]: (_extractor) => ({
        display: "DEFAULT_VISIBLE",
        getFormattedValue: (row) => <EntitiesCell entities={row.entities} />,
    }),
};
function buildControlProgramColumnBuilders(
    buildActivitiesUrl: (controlProgramId: ControlProgramId) => string
): {
    [extractorId: string]: ColumnBuilder<ControlProgramDataRow, any>;
} {
    return {
        ...CONTROL_PROGRAM_COLUMN_BUILDERS,
        [CP_EXTRACTOR_KEYS.CONTROL_RESULT_COUNT]: (_extractor) => ({
            getFormattedValue: (row) => {
                const { controlProgram, controlResultCount } =
                    row.controlProgram.resource;
                return (
                    <ProtectedLink
                        href={buildActivitiesUrl(controlProgram.id)}
                        hasAccess={row.controlProgram.actions.view}
                    >
                        {controlResultCount}
                    </ProtectedLink>
                );
            },
            layout: { fixedWidth: 110 },
        }),
    };
}

function buildNameExtractor(
    rows: ControlProgramDataRow[]
): DataExtractor<
    ControlProgramDataRow,
    string,
    IdAndName<ControlProgramId, string>
> {
    const cp = (row: ControlProgramDataRow) =>
        row.controlProgram.resource.controlProgram;

    const mapIdToName: Record<number, string> = {};
    rows.forEach((row) => {
        mapIdToName[cp(row).id] = cp(row).name;
    });

    const formatValue = (row: ControlProgramDataRow) => cp(row).name;

    return {
        id: CP_EXTRACTOR_KEYS.NAME,
        label: t("ControlManagement_ControlProgramName"),
        getValue: formatValue,
        compare: Cmp.map(formatValue, Cmp.string()),
        asString: formatValue,
        customFilterTransformer: {
            getValue: (row) => ({ id: cp(row).id, name: cp(row).name }),
            asString: (idAndName: IdAndName<ControlProgramId, string>) =>
                mapIdToName[idAndName.id] || idAndName.name,
        },
    };
}

function buildStatusExtractor(): DataExtractor<
    ControlProgramDataRow,
    ControlProgramStatus,
    boolean
> {
    function extractRunning(row: ControlProgramDataRow): boolean {
        return row.controlProgram.resource.controlProgram.running;
    }

    function boolToStatus(running: boolean): ControlProgramStatus {
        return running ? "Active" : "Inactive";
    }

    return {
        id: CP_EXTRACTOR_KEYS.STATUS,
        label: t("AccessAndAssignment_ControlPrograms_Status"),
        getValue: (row) => boolToStatus(extractRunning(row)),
        compare: Cmp.map(extractRunning, Cmp.primitive),
        asString: (row) =>
            formatControlProgramStatus(boolToStatus(extractRunning(row))),
        customFilterTransformer: {
            getValue: extractRunning,
            asString: (isRunning) =>
                formatControlProgramStatus(boolToStatus(isRunning)),
        },
    };
}

function buildTagsExtractor<
    TAG extends AbstractTag,
    CAT extends AbstractTagCategory,
>(
    allTags: TagLookupData<TAG, CAT>
): DataExtractor<ControlProgramDataRow, TagsData, IdAndName<string, string>> {
    const tagNameLookup = createTagNameLookupFn(allTags);

    return {
        id: CP_EXTRACTOR_KEYS.TAGS,
        label: t("Tags"),
        getValue: (row) => row.tags,
        compare: Cmp.map((row) => row.tags.tags.length, Cmp.primitive),
        asString: (_row) => {
            throw new Error("Unexpected call of asString() on tags extractor.");
        },
        customFilterTransformer: {
            getValue: (_row) => {
                throw new Error(
                    "Unexpected call of customFilterTransformer.getValue() on tags extractor."
                );
            },
            asString: tagNameLookup,
        },
    };
}

function buildTagCategoriesExtractor<
    TAG extends AbstractTag,
    CAT extends AbstractTagCategory,
>(
    allTags: TagLookupData<TAG, CAT>
): DataExtractor<ControlProgramDataRow, TagsData, IdAndName<string, string>> {
    const categoryNameLookup = createCategoryNameLookupFn(allTags);

    return {
        id: CP_EXTRACTOR_KEYS.TAG_CATEGORIES,
        label: t("Search_Tag_Categories"),
        getValue: (row) => row.tags,
        compare: (a, b) =>
            a.tags.tagCategories.length - b.tags.tagCategories.length,
        asString: (_row) => "",
        customFilterTransformer: {
            getValue: (_row) => {
                // Every control row has multiple tag categories.
                // For this case customFilterTransformer.getValue of expander is used.
                // See definition of buildTagCategoryExtractorExpanderForNonGroupedTagsWithIds()
                throw new Error(
                    "Unexpected call of customFilterTransformer.getValue() on tagCategories extractor."
                );
            },
            asString: categoryNameLookup,
        },
    };
}

function buildControlCountExtractor(): DataExtractor<
    ControlProgramDataRow,
    number
> {
    const extractValue = (row: ControlProgramDataRow) =>
        row.controlProgram.resource.controlCount;
    const formatValue = (controlCount: number) => controlCount.toString();
    const extractAndFormatValue = (row: ControlProgramDataRow) =>
        formatValue(extractValue(row));

    return {
        id: CP_EXTRACTOR_KEYS.CONTROL_COUNT,
        label: t("RiskMapResponse_Columns_Controls"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildControlResultsCountExtractor(): DataExtractor<
    ControlProgramDataRow,
    number
> {
    const extractValue = (row: ControlProgramDataRow) =>
        row.controlProgram.resource.controlResultCount;
    const formatValue = (controlResultCount: number) =>
        controlResultCount.toString();
    const extractAndFormatValue = (row: ControlProgramDataRow) =>
        formatValue(extractValue(row));

    return {
        id: CP_EXTRACTOR_KEYS.CONTROL_RESULT_COUNT,
        label: t("ControlResultsCount"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

class ControlProgramsListComp extends React.PureComponent<
    ControlProgramsListProps,
    ControlProgramsListCompState
> {
    constructor(props: ControlProgramsListProps) {
        super(props);
        this.state = {
            status: { type: "DEFAULT" },
        };
    }

    private memoizeColumnBuilders = memoizeOne(
        buildControlProgramColumnBuilders
    );

    private asyncComponent =
        React.createRef<
            AsyncComponent2<ControlProgramOverviewResources, ControlProgramTags>
        >();

    private memoizeDataView: (
        controlProgramOverviews: ControlProgramOverviewResources,
        controlProgramTags: ControlProgramTags,
        filter: SimpleAndFilter | null,
        controlEntitiesEnabled: boolean
    ) => [
        DataView<ControlProgramDataRow>,
        DataViewIndex<ControlProgramDataRow>,
    ] = memoizeOne(
        (
            controlProgramOverviews,
            controlProgramTags,
            filter,
            controlEntitiesEnabled
        ) => {
            const {
                linkedResources: { tags, tagCategories, users, entities },
            } = controlProgramOverviews;

            // Build entity lookup map
            const entityMap = new Map(entities.map((e) => [e.id, e]));

            const adaptedTags = adaptTags(tags.map((tag) => tag.resource));
            const data: Array<ControlProgramDataRow> =
                controlProgramOverviews.content.map((controlProgram) => {
                    const tags: Array<Tag> = controlProgram.resource.tagIds.map(
                        (id) => adaptedTags.find((t) => t.id === id)!
                    );

                    const { admin, edit, view } =
                        controlProgram.resource.accessRights;
                    const userIdsToUsers = (userIds: UserId[]) =>
                        userIds.map(
                            (userId) => users.find((u) => u.id === userId)!
                        );
                    const usersByAccessRight = new Map([
                        [ACCESS_RIGHTS.ADMIN, userIdsToUsers(admin)],
                        [ACCESS_RIGHTS.EDIT, userIdsToUsers(edit)],
                        [ACCESS_RIGHTS.VIEW, userIdsToUsers(view)],
                    ]);

                    // Map entity IDs to EntityView objects, sorted by name
                    const programEntities: EntityView[] =
                        controlProgram.resource.controlEntityIds
                            .map((id) => entityMap.get(id))
                            .filter((e) => e !== undefined)
                            .sort((a, b) => a.name.localeCompare(b.name));

                    return {
                        controlProgram,
                        accessRights: { usersByAccessRight },
                        tags: {
                            tagCategories,
                            tags,
                        },
                        entities: programEntities,
                    };
                });

            // Conditionally build entity extractors based on feature flag
            const entitiesExtractorsAndExpanders =
                controlEntitiesEnabled && entities.length > 0
                    ? buildExtractorsAndExpandersForEntities(entities)
                    : null;

            const standardExtractors: Array<
                DataExtractor<ControlProgramDataRow, unknown>
            > = [
                downgradeExtractorType(buildNameExtractor(data)),
                downgradeExtractorType(buildControlCountExtractor()),
                downgradeExtractorType(buildStatusExtractor()),
                {
                    id: CP_EXTRACTOR_KEYS.ACCESS_RIGHTS,
                    label: t("AccessRights_AccessRightsColumn"),
                    getValue: (row) => row.accessRights,
                    compare: Cmp.map(
                        (row) =>
                            [...row.accessRights.usersByAccessRight.values()]
                                .map((users) => users.length)
                                .reduce((acc, length) => acc + length, 0),
                        Cmp.primitive
                    ),
                    asString: (_row) => "",
                },
                ...(entitiesExtractorsAndExpanders
                    ? entitiesExtractorsAndExpanders.extractors.map(
                          downgradeExtractorType
                      )
                    : []),
                downgradeExtractorType(buildTagsExtractor(controlProgramTags)),
                downgradeExtractorType(
                    buildTagCategoriesExtractor(controlProgramTags)
                ),
                downgradeExtractorType(buildControlResultsCountExtractor()),
            ];

            const categoryExtractorsAndExpanders =
                buildCategoryExtractorsAndExpandersFromTagsAndFilter(
                    controlProgramTags,
                    filter
                );

            const {
                extractors: accessRightsExtractors,
                expanders: accessRightsExpanders,
            } = buildExtractorsAndExpandersForAccessRights(users);

            const dataView: DataView<ControlProgramDataRow> = {
                dataSource: new ArrayDataSource(data, (row) =>
                    row.controlProgram.resource.controlProgram.id.toString()
                ),
                extractorGroups: [
                    {
                        id: "standard",
                        label: "Standard",
                        extractors: standardExtractors,
                    },
                    {
                        id: "category",
                        label: "Category",
                        extractors: categoryExtractorsAndExpanders.extractors,
                    },
                    {
                        id: "accessRightsGroup",
                        label: "Access Rights",
                        extractors: accessRightsExtractors,
                    },
                ],
                sorters: [
                    { extractorId: CP_EXTRACTOR_KEYS.NAME, direction: "ASC" },
                ],
                defaultCompareItems: Cmp.map(
                    (row) => row.controlProgram.resource.controlProgram.id,
                    Cmp.primitive
                ),
            };

            const expanders: Array<ExtractorExpander<any, any>> = [
                ...categoryExtractorsAndExpanders.expanders,
                ...accessRightsExpanders,
                ...[
                    buildTagCategoryExtractorExpanderForNonGroupedTagsWithIds(
                        controlProgramTags
                    ),
                ],
                ...(entitiesExtractorsAndExpanders?.expanders || []),
            ];

            // The filter should be able to search on all columns
            const indexedExtractorIds = [
                CP_EXTRACTOR_KEYS.NAME,
                CP_EXTRACTOR_KEYS.STATUS,
                CP_EXTRACTOR_KEYS.CONTROL_COUNT,
                CP_EXTRACTOR_KEYS.TAG_CATEGORIES,
                CP_EXTRACTOR_KEYS.CONTROL_RESULT_COUNT,
                ...categoryExtractorsAndExpanders.extractors.map(
                    (extractor) => extractor.id
                ),
                ...accessRightsExtractors.map((extractor) => extractor.id),
                ...(entitiesExtractorsAndExpanders?.extractors.map(
                    (extractor) => extractor.id
                ) || []),
            ];

            return [
                dataView,
                new DataViewIndex(dataView, indexedExtractorIds, expanders, []),
            ];
        }
    );

    private changeDetailsDrawerBody = (
        selectedControlPrograms: Array<ControlProgramDataRow>,
        readOnly: boolean,
        controlProgramTags: ControlProgramTags,
        form: WrappedFormUtils<ChangeDetailsFormValues, ChangeDetailsFormValues>
    ) => {
        return (
            <ChangeDetailsForm
                readOnly={readOnly}
                controlProgramTags={controlProgramTags}
                form={form}
                initialDescriptions={this.changeDetailsSelectedProgramDescriptions(
                    selectedControlPrograms
                )}
                initialTags={this.changeDetailsSelectedProgramTags(
                    selectedControlPrograms
                )}
            />
        );
    };

    private changeDetailsSelectedProgramDescriptions = memoizeOne(
        (
            selectedControlPrograms: Array<ControlProgramDataRow>
        ): Array<string> => {
            return selectedControlPrograms.map(
                (row) =>
                    row.controlProgram.resource.controlProgram.description || ""
            );
        }
    );

    private changeDetailsSelectedProgramTags = memoizeOne(
        (
            selectedControlPrograms: Array<ControlProgramDataRow>
        ): Array<Set<Guid>> => {
            return selectedControlPrograms.map(
                (row) => new Set(row.controlProgram.resource.tagIds)
            );
        }
    );

    private saveDetails = (
        selectedIds: Array<string>,
        form: WrappedFormUtils<
            ChangeDetailsFormValues,
            ChangeDetailsFormValues
        >,
        done: (closeDrawer: boolean) => void
    ) => {
        const { ajaxManager } = this.props;
        const patch = form.getFieldValue("patch");
        if (patch === undefined) {
            throw new Error("Unexpected undefined patch");
        }
        if (patch.description === undefined && patch.tags === undefined) {
            // No changes, just close the drawer
            done(true);
            return;
        }
        const update: UpdatableControlPrograms = {
            controlProgramIds: selectedIds.map(Number),
            description: patch.description,
            tags: patch.tags,
        };
        ajaxManager
            .ajax<void, ControlProgramsListCompStatus>(
                () => patchControlPrograms(update),
                {
                    component: this,
                    initialStatusValue: "DEFAULT",
                    inFlightStatus: { type: "SUBMITTING" },
                    getSuccessStatus: (_resp) => ({ type: "DEFAULT" }),
                    getErrorStatus: (_code) => ({ type: "DEFAULT" }),
                }
            )
            .then(() => {
                if (this.asyncComponent.current) {
                    this.updateChangedDetails(
                        selectedIds,
                        patch,
                        this.asyncComponent.current
                    );
                }
                done(true);
                emitNotification(
                    "success",
                    t("ControlPrograms_Successfully_Updated")
                );
            })
            .catch((e) => {
                console.log(e);
                done(false);
            });
    };

    private updateChangedDetails = (
        selectedIds: Array<string>,
        patch: Pick<UpdatableControlPrograms, "description" | "tags">,
        asyncComponent: AsyncComponent2<
            ControlProgramOverviewResources,
            ControlProgramTags
        >
    ) => {
        const [oldOverviews, allCPTags] = asyncComponent.getData()!;
        const newOverviews = set(oldOverviews, {
            content: oldOverviews.content.map(
                ({ resource: oldCp, actions }) => {
                    if (
                        selectedIds.indexOf(
                            oldCp.controlProgram.id.toString()
                        ) >= 0
                    ) {
                        const newCp = clone(oldCp);
                        if (patch.description !== undefined) {
                            newCp.controlProgram.description =
                                patch.description;
                        }
                        if (patch.tags !== undefined) {
                            let newTagIds = oldCp.tagIds.slice();
                            Object.entries(patch.tags).forEach(
                                ([tagId, presence]): void => {
                                    switch (presence) {
                                        case "Present":
                                            if (
                                                !(newTagIds.indexOf(tagId) >= 0)
                                            ) {
                                                newTagIds.push(tagId);
                                            }
                                            break;
                                        case "Absent":
                                            break;
                                        default:
                                            return assertNever(presence);
                                    }
                                }
                            );
                            newTagIds = newTagIds.filter(
                                (tagId) =>
                                    !(
                                        patch.tags &&
                                        patch.tags[tagId] === "Absent"
                                    )
                            );
                            newCp.tagIds = newTagIds;
                        }
                        return { resource: newCp, actions };
                    } else {
                        return { resource: oldCp, actions };
                    }
                }
            ),
            linkedResources: set(oldOverviews.linkedResources, {
                // Always update tags and categories
                tagCategories: allCPTags.linkedResources,
                tags: allCPTags.content,
            }),
        });
        asyncComponent.updateData1(newOverviews);
    };

    private manageAccessRightsDrawerBody = (
        selectedControlPrograms: Array<ControlProgramDataRow>,
        readOnly: boolean,
        form: WrappedFormUtils<
            ManageAccessRightsFormValues,
            ManageAccessRightsFormValues
        >
    ) => {
        return (
            <ManageAccessRightsForm
                readOnly={readOnly}
                form={form}
                initialUserAndRights={this.manageAccessRightsSelectedProgramUserAndRights(
                    selectedControlPrograms
                )}
            />
        );
    };

    private manageAccessRightsSelectedProgramUserAndRights = memoizeOne(
        (
            selectedControlPrograms: Array<ControlProgramDataRow>
        ): Array<Array<UserAndRights<ControlProgramAccessRights>>> => {
            return selectedControlPrograms.map((row) => {
                const userAndRights = [];
                for (const [
                    accessRight,
                    users,
                ] of row.accessRights.usersByAccessRight.entries()) {
                    for (const user of users) {
                        userAndRights.push({ user, accessRight });
                    }
                }
                return userAndRights;
            });
        }
    );

    private saveAccessRights = (
        selectedIds: Array<string>,
        form: WrappedFormUtils<
            ManageAccessRightsFormValues,
            ManageAccessRightsFormValues
        >,
        done: (closeDrawer: boolean) => void
    ) => {
        const { ajaxManager } = this.props;
        const userAndRights = form.getFieldValue("userAndRights");
        if (userAndRights === undefined) {
            throw new Error("Unexpected undefined userAndRights");
        }
        const patch = form.getFieldValue("patch");
        if (patch === undefined) {
            throw new Error("Unexpected undefined patch");
        }
        if (patch.accessRights === undefined) {
            // No changes, just close the drawer
            done(true);
            return;
        }
        const update: UpdatableControlPrograms = {
            controlProgramIds: selectedIds.map(Number),
            accessRights: patch.accessRights,
        };
        ajaxManager
            .ajax<void, ControlProgramsListCompStatus>(
                () => patchControlPrograms(update),
                {
                    component: this,
                    initialStatusValue: "DEFAULT",
                    inFlightStatus: { type: "SUBMITTING" },
                    getSuccessStatus: (_resp) => ({ type: "DEFAULT" }),
                    getErrorStatus: (_code) => ({ type: "DEFAULT" }),
                }
            )
            .then(() => {
                if (this.asyncComponent.current) {
                    this.updateManagedAccessRights(
                        selectedIds,
                        patch,
                        userAndRights.userAndRights,
                        this.asyncComponent.current
                    );
                }
                done(true);
                emitNotification(
                    "success",
                    t("ControlPrograms_Successfully_Updated")
                );
            })
            .catch((e) => {
                console.log(e);
                done(false);
            });
    };

    private updateManagedAccessRights = (
        selectedIds: Array<string>,
        patch: Pick<UpdatableControlPrograms, "accessRights">,
        userAndRights: Array<Array<UserAndRights<ControlProgramAccessRights>>>,
        asyncComponent: AsyncComponent2<
            ControlProgramOverviewResources,
            ControlProgramTags
        >
    ) => {
        const [oldOverviews] = asyncComponent.getData()!;
        const newControlPrograms = oldOverviews.content.map(
            ({ resource: oldCp, actions }) => {
                if (
                    selectedIds.indexOf(oldCp.controlProgram.id.toString()) >= 0
                ) {
                    const newCp = clone(oldCp);
                    if (patch.accessRights !== undefined) {
                        // Keep any user not referenced in the patch
                        const newAccessRights: AdminEditViewAccessRights = {
                            admin: oldCp.accessRights.admin.filter(
                                (userId) =>
                                    patch.accessRights![userId] === undefined
                            ),
                            edit: oldCp.accessRights.edit.filter(
                                (userId) =>
                                    patch.accessRights![userId] === undefined
                            ),
                            view: oldCp.accessRights.view.filter(
                                (userId) =>
                                    patch.accessRights![userId] === undefined
                            ),
                        };
                        // Add the changes
                        Object.entries(patch.accessRights).forEach(
                            ([userIdStr, ar]): void => {
                                const userId: UserId = Number(userIdStr);
                                switch (ar) {
                                    case "Admin":
                                        if (
                                            !(
                                                newAccessRights.admin.indexOf(
                                                    userId
                                                ) >= 0
                                            )
                                        ) {
                                            newAccessRights.admin.push(userId);
                                        }
                                        break;
                                    case "Edit":
                                        if (
                                            !(
                                                newAccessRights.edit.indexOf(
                                                    userId
                                                ) >= 0
                                            )
                                        ) {
                                            newAccessRights.edit.push(userId);
                                        }
                                        break;
                                    case "View":
                                        if (
                                            !(
                                                newAccessRights.view.indexOf(
                                                    userId
                                                ) >= 0
                                            )
                                        ) {
                                            newAccessRights.view.push(userId);
                                        }
                                        break;
                                    case null:
                                        newAccessRights.admin =
                                            newAccessRights.admin.filter(
                                                (uid) => uid !== userId
                                            );
                                        newAccessRights.edit =
                                            newAccessRights.edit.filter(
                                                (uid) => uid !== userId
                                            );
                                        newAccessRights.view =
                                            newAccessRights.view.filter(
                                                (uid) => uid !== userId
                                            );
                                        break;
                                    default:
                                        return assertNever(ar);
                                }
                            }
                        );
                        newCp.accessRights = newAccessRights;
                    }
                    return { resource: newCp, actions };
                } else {
                    return { resource: oldCp, actions };
                }
            }
        );
        const newUsers = oldOverviews.linkedResources.users;
        const userIds = new Set<UserId>();
        oldOverviews.linkedResources.users.forEach((user) => {
            userIds.add(user.id);
        });
        userAndRights.forEach((uars) => {
            uars.forEach(({ user }) => {
                if (!userIds.has(user.id)) {
                    newUsers.push(user);
                    userIds.add(user.id);
                }
            });
        });
        const newOverviews = set(oldOverviews, {
            content: newControlPrograms,
            linkedResources: set(oldOverviews.linkedResources, {
                users: newUsers,
            }),
        });
        asyncComponent.updateData1(newOverviews);
    };

    private renderLoading = () => <PageSpinner />;

    private renderError = (code: number | undefined) => {
        return (
            <OrgApplicationBar title={t("ControlPrograms")}>
                <LoggedInHTTPError code={code} />
            </OrgApplicationBar>
        );
    };

    private renderContent = (
        controlProgramOverviews: ControlProgramOverviewResources,
        controlProgramTags: ControlProgramTags
    ) => {
        const renderContentInner = ({
            pageState,
            onPageStateChange,
            onCreateShareableLink,
        }: RenderContentArgs<PageFilterAndColumns>) => {
            const {
                ajaxManager,
                orgCookie,
                orgProfileCookie,
                availableActions,
            } = this.props;

            const { filter: storedFilter, onFilterChange } =
                splitPageStateWithCallback(pageState, onPageStateChange);
            const filter = adjustFiltersWithCategoryExtractorId(
                controlProgramTags,
                storedFilter
            );
            const [dataView, dataViewIndex] = this.memoizeDataView(
                controlProgramOverviews,
                controlProgramTags,
                filter,
                this.props.controlEntitiesEnabled
            );

            return (
                <ControlModuleApplicationBar
                    title={t("ControlPrograms")}
                    page={PAGE_CONTROL_PROGRAMS}
                    breadcrumbs={[]}
                    availableActions={availableActions}
                    onCreateShareableLink={onCreateShareableLink}
                >
                    <ControlResources<
                        ControlProgramDataRow,
                        object,
                        ChangeDetailsFormValues,
                        ManageAccessRightsFormValues,
                        object
                    >
                        ajaxManager={ajaxManager}
                        availableActions={availableActions}
                        orgCookie={orgCookie}
                        orgProfileCookie={orgProfileCookie}
                        dataView={dataView}
                        dataViewIndex={dataViewIndex}
                        tableMode={DATA_TABLE_MODE_VIEWPORT}
                        filter={filter}
                        onFilterChanged={onFilterChange}
                        columnBuilders={this.memoizeColumnBuilders(
                            this.props.buildActivitiesUrl
                        )}
                        changeDetails={{
                            canEditItem: (item) =>
                                item.controlProgram.actions.edit.content,
                            readOnlyInfo: t("ChangeDetails_ReadOnlyInfo"),
                            renderDrawerBody: (
                                selectedControlPrograms,
                                readOnly,
                                form
                            ) =>
                                this.changeDetailsDrawerBody(
                                    selectedControlPrograms,
                                    readOnly,
                                    controlProgramTags,
                                    form
                                ),
                            confirmationTitle: t(
                                "ControlProgram_ChangeDetails_ConfirmationTitle"
                            ),
                            confirmationText: (selectedIds) =>
                                t(
                                    "ControlProgram_ChangeDetails_ConfirmationText",
                                    {
                                        count: selectedIds.length,
                                    }
                                ),
                            onSave: this.saveDetails,
                        }}
                        manageAccessRights={{
                            canAdminItem: (item) =>
                                item.controlProgram.actions.edit.accessRights,
                            readOnlyInfo: t(
                                "ControlProgram_ManageAccessRights_ReadOnlyInfo"
                            ),
                            renderDrawerBody: (
                                selectedControlPrograms,
                                readOnly,
                                form
                            ) =>
                                this.manageAccessRightsDrawerBody(
                                    selectedControlPrograms,
                                    readOnly,
                                    form
                                ),
                            confirmationTitle: t(
                                "ControlProgram_ChangeDetails_ConfirmationTitle"
                            ),
                            confirmationText: (selectedIds) =>
                                t(
                                    "ControlProgram_ChangeDetails_ConfirmationText",
                                    {
                                        count: selectedIds.length,
                                    }
                                ),
                            onSave: this.saveAccessRights,
                        }}
                    />
                </ControlModuleApplicationBar>
            );
        };

        return (
            <PageStateManager<"ControlPrograms", PageFilterAndColumns>
                pageKey={PAGE_KEY.controlPrograms()}
                renderLoading={this.renderLoading}
                renderError={this.renderError}
                codecs={pageStateControlProgramsCodecs}
                renderContent={renderContentInner}
            />
        );
    };

    render = () => {
        const { ajaxManager } = this.props;

        return (
            <AsyncComponent2<
                ControlProgramOverviewResources,
                ControlProgramTags
            >
                ref={this.asyncComponent}
                ajaxManager={ajaxManager}
                promises={[
                    () =>
                        getControlPrograms().then((response) => ({
                            ...response,
                            data: response.data,
                        })),
                    () => getControlProgramsTags(),
                ]}
                key={"control-programs-async"}
                renderLoading={this.renderLoading}
                renderError={this.renderError}
                renderContent={this.renderContent}
            />
        );
    };
}

const ControlProgramsWithI18n = withI18n<ControlProgramsListProps>(
    ControlProgramsListComp
);

type ControlProgramsWrapperProps = Omit<
    ControlProgramsListProps,
    "controlEntitiesEnabled" | "buildActivitiesUrl"
>;

function ControlPrograms(props: ControlProgramsWrapperProps) {
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const buildActivitiesUrl = React.useCallback(
        (controlProgramId: ControlProgramId) =>
            buildControlActivitiesListUrl({
                type: "controlProgram",
                controlProgramId,
            }),
        []
    );

    return (
        <ControlProgramsWithI18n
            ajaxManager={props.ajaxManager}
            orgCookie={props.orgCookie}
            orgProfileCookie={props.orgProfileCookie}
            availableActions={props.availableActions}
            controlEntitiesEnabled={controlEntitiesEnabled}
            buildActivitiesUrl={buildActivitiesUrl}
        />
    );
}

interface ExtractorsAndExpandersForAccessRights {
    extractors: Array<
        DataExtractor<
            ControlProgramDataRow,
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<number, string>
        >
    >;
    expanders: Array<
        ExtractorExpander<
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<number, string>
        >
    >;
}

function buildExtractorsAndExpandersForAccessRights(
    users: UserOverview[]
): ExtractorsAndExpandersForAccessRights {
    const extractors: Array<
        DataExtractor<
            ControlProgramDataRow,
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<UserId, string>
        >
    > = [];
    const expanders: Array<
        ExtractorExpander<
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<UserId, string>
        >
    > = [];

    const mapIdToName: Record<UserId, string> = {};
    users.forEach((user) => {
        mapIdToName[user.id] = formatUserName(user);
    });

    const asString = (userIdAndName: IdAndName<UserId, string>) =>
        mapIdToName[userIdAndName.id] || userIdAndName.name;

    ALL_ACCESS_RIGHTS.forEach((accessRight: StandardAccessRight) => {
        const getUser = (row: ControlProgramDataRow) =>
            row.accessRights.usersByAccessRight.get(accessRight) || [];

        const extractorId = accessRightCategoryExtractorId(accessRight);

        const extractor: DataExtractor<
            ControlProgramDataRow,
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<UserId, string>
        > = {
            id: extractorId,
            label: formatAccessRight(accessRight),
            getValue: (row) => row.accessRights.usersByAccessRight,
            compare: Cmp.map(
                getUser,
                Cmp.map((extrs) => extrs.map(formatUserName).join(","))
            ),
            asString: (row) => getUser(row).map(formatUserName).join(", "),
            customFilterTransformer: {
                getValue: (_row) => {
                    throw new Error(
                        `Unexpected call of customFilterTransformer.getValue() on ${extractorId} extractor.`
                    );
                },
                asString,
            },
        };
        extractors.push(extractor);

        const expander: ExtractorExpander<
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<UserId, string>
        > = {
            extractorId,
            expand: (
                usersByAccessRight: Map<StandardAccessRight, UserOverview[]>
            ) => {
                const results: Array<PartialData<IdAndName<UserId, string>>> =
                    [];

                const users: Array<UserOverview> =
                    usersByAccessRight.get(accessRight) || [];
                users.forEach((user: UserOverview) => {
                    const userIdAndName: IdAndName<UserId, string> = {
                        id: user.id,
                        name: formatUserName(user),
                    };
                    results.push({
                        partialId: `${accessRightCategoryExtractorId(
                            accessRight
                        )}-${user.id}`,
                        expandedValue: userIdAndName,
                    });
                });
                return results;
            },
            compare: Cmp.map(asString, Cmp.string()),
            asString,
            customFilterTransformer: {
                getValue: (userIdAndName) => userIdAndName,
                asString,
            },
        };

        expanders.push(expander);
    });

    return {
        extractors,
        expanders,
    };
}

interface ExtractorsAndExpandersForEntities {
    extractors: Array<
        DataExtractor<
            ControlProgramDataRow,
            EntityView[],
            IdAndName<number, string>
        >
    >;
    expanders: Array<
        ExtractorExpander<EntityView[], IdAndName<number, string>>
    >;
}

function buildExtractorsAndExpandersForEntities(
    allEntities: EntityView[]
): ExtractorsAndExpandersForEntities {
    const mapIdToName: Record<number, string> = {};
    allEntities.forEach((entity) => {
        mapIdToName[entity.id] = entity.name;
    });

    const asString = (idAndName: IdAndName<number, string>) =>
        mapIdToName[idAndName.id] || idAndName.name;

    const extractor: DataExtractor<
        ControlProgramDataRow,
        EntityView[],
        IdAndName<number, string>
    > = {
        id: ENTITIES_EXTRACTOR_ID,
        label: t("EntityDefinition_Title"),
        getValue: (row) => row.entities,
        compare: Cmp.map((row) => row.entities.length, Cmp.primitive),
        asString: (row) => row.entities.map((e) => e.name).join(", "),
        customFilterTransformer: {
            getValue: (_row) => {
                throw new Error(
                    `Unexpected call of customFilterTransformer.getValue() on ${ENTITIES_EXTRACTOR_ID} extractor.`
                );
            },
            asString,
        },
    };

    const expander: ExtractorExpander<
        EntityView[],
        IdAndName<number, string>
    > = {
        extractorId: ENTITIES_EXTRACTOR_ID,
        expand: (entities: EntityView[]) => {
            return entities.map((entity) => ({
                partialId: `${ENTITIES_EXTRACTOR_ID}-${entity.id}`,
                expandedValue: { id: entity.id, name: entity.name },
            }));
        },
        compare: Cmp.map(asString, Cmp.string()),
        asString,
        customFilterTransformer: {
            getValue: (idAndName) => idAndName,
            asString,
        },
    };

    return {
        extractors: [extractor],
        expanders: [expander],
    };
}

export { ControlPrograms };
